import { Box, styled, useTheme } from '@mui/material';
import { Typography } from '../../components/Typography/Typography';
import { FC, ReactNode } from 'react';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { Breadcrumb } from '../../types';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../state/UIState';

const Wrapper = styled('div')<{isSmallScreen: boolean}>`
  padding: ${({isSmallScreen}) => isSmallScreen ? '4px 16px' : '28px 36px'};
  margin-top: ${({isSmallScreen}) => isSmallScreen ? '48px' : '0px'}
`;

const MainSection = styled('div')<{ $alignTop?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: ${({ $alignTop }) => ($alignTop ? 'flex-start' : 'center')};
  justify-content: space-between;
  gap: 16px;
`;

const TitleAndSubtitleWrapper = styled('div')`
  flex: 1;
  min-width: 0;
`;

interface Props {
  title: string | JSX.Element;
  description?: string;
  breadcrumbs: Breadcrumb[];
  actionComponent?: React.ReactNode;
  /** Same horizontal row as breadcrumbs, right-aligned (e.g. month prev/next). */
  breadcrumbEndActions?: ReactNode;
  /** Renders on the same row as the title (e.g. summary chips), after the title text. */
  titleEndAdornment?: ReactNode;
  /** Renders below the title row (e.g. profile avatar + name). */
  belowTitle?: ReactNode;
}

export const PageHeader: FC<Props> = ({
  title,
  description,
  breadcrumbs,
  actionComponent,
  breadcrumbEndActions,
  titleEndAdornment,
  belowTitle,
}) => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);

  return (
    <Wrapper isSmallScreen={isSmallScreen}>
      {isSmallScreen && breadcrumbEndActions && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1,
            marginBottom: '12px',
          }}
        >
          {breadcrumbEndActions}
        </Box>
      )}
      {!isSmallScreen &&
        (breadcrumbEndActions ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              columnGap: 2,
              rowGap: 1,
              marginBottom: '24px',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Breadcrumbs breadcrumbs={breadcrumbs} disableBottomMargin />
            </Box>
            <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: 2 }}>
              {breadcrumbEndActions}
            </Box>
          </Box>
        ) : (
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        ))}
      <MainSection $alignTop={!!belowTitle}>
        <TitleAndSubtitleWrapper>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: '10px',
              rowGap: '8px',
              marginBottom: belowTitle || description ? '8px' : '4px',
            }}
          >
            <Typography
              variant='h1'
              color={colors.text.main}
              style={{ fontSize: '32px', lineHeight: '42px', marginBottom: 0 }}
            >
              {title}
            </Typography>
            {titleEndAdornment ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                {titleEndAdornment}
              </Box>
            ) : null}
          </Box>
          {belowTitle && <Box sx={{ marginBottom: description ? '8px' : 0 }}>{belowTitle}</Box>}
          {description && (
            <Typography variant='body' color={colors.text.secondary}>
              {description}
            </Typography>
          )}
        </TitleAndSubtitleWrapper>
        {actionComponent && actionComponent}
      </MainSection>
    </Wrapper>
  );
};
